import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { EmailRegex, GenericEmailRegex, PasswordReg } from "src/common/constants/regex.constants";

export function emailValidator(control: AbstractControl): { [key: string]: any } | null {
  if (isEmptyInputValue(control.value)) {
    // if nothing has been passed then let other validator catch it
    return null;
  }
  const isValidEmail = EmailRegex.test(control.value.trim());
  return isValidEmail ? null : { invalidEmail: true };
}

export function genericEmailValidator(control: AbstractControl): { [key: string]: any } | null {
  if (isEmptyInputValue(control.value)) {
    // if nothing has been passed then let other validator catch it
    return null;
  }
  const isValidEmail = !GenericEmailRegex.test(control.value.trim());
  return isValidEmail ? null : { invalidEmail: true };
}

export function passwordValidator(control: AbstractControl): { [key: string]: any } | null {
  if (isEmptyInputValue(control.value)) {
    // if nothing has been passed then let other validator catch it
    return null;
  }
  const isValidPassword = PasswordReg.test(control.value.trim());
  return isValidPassword ? null : { invalidPassword: true };
}

export function trimRequiredValidator(control: AbstractControl): { [key: string]: any } | null {
  const isValid = !!(
    control.value &&
    typeof control.value === "string" &&
    control.value.trim().length > 0
  );
  return isValid ? null : { required: { value: control.value } };
}

function isEmptyInputValue(value: any): boolean {
  return (
    value == null || value.length === 0 || (typeof value === "string" && value.trim().length === 0)
  );
}

export function validateConfirmPassword(control: AbstractControl): boolean {
  const passwordControl = control.root.get("password");
  if (!passwordControl) {
    return false;
  }
  if (passwordControl.value !== control.value) {
    return false;
  }
  return true;
}

/**
 * Validator for select fields that treats "null", "all", empty string, or actual null as invalid
 */
export function selectRequiredValidator(control: AbstractControl): { [key: string]: any } | null {
  const value = control.value;
  const isInvalid =
    value === null || value === undefined || value === "" || value === "null" || value === "all";
  return isInvalid ? { required: { value: control.value } } : null;
}

export function mergeFieldsValidator(allowed: string[]) {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw == null || typeof raw !== "string" || raw.length === 0) {
      return null;
    }

    const plain = htmlToPlainText(raw);
    if (plain.trim().length === 0) {
      return null;
    }

    const allowedSet = new Set(allowed);

    const malformed = findMalformedMergeFields(plain);
    if (malformed.length > 0) {
      return { malformedMergeField: { tokens: Array.from(new Set(malformed)) } };
    }

    const tokenRegex = /\{\{([^{}]*?)\}\}/g;
    const invalid: string[] = [];
    let m: RegExpExecArray | null;
    while ((m = tokenRegex.exec(plain)) !== null) {
      const token = `{{${m[1]}}}`;
      if (!allowedSet.has(token)) {
        invalid.push(token);
      }
    }
    if (invalid.length > 0) {
      return { invalidMergeField: { tokens: Array.from(new Set(invalid)) } };
    }

    return null;
  };
}

function htmlToPlainText(html: string): string {
  if (typeof document === "undefined") {
    return html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'");
  }
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent ?? "";
}

function findMalformedMergeFields(plain: string): string[] {
  const MAX_LEN = 30;
  const openStack: number[] = [];
  const orphanCloses: number[] = [];

  for (let i = 0; i < plain.length - 1; ) {
    if (plain[i] === "{" && plain[i + 1] === "{") {
      openStack.push(i);
      i += 2;
    } else if (plain[i] === "}" && plain[i + 1] === "}") {
      if (openStack.length > 0) {
        openStack.pop();
      } else {
        orphanCloses.push(i);
      }
      i += 2;
    } else {
      i++;
    }
  }

  const malformed: string[] = [];

  openStack.forEach((pos) => {
    const nextOpen = plain.indexOf("{{", pos + 2);
    const end = nextOpen === -1 ? plain.length : nextOpen;
    malformed.push(plain.substring(pos, Math.min(end, pos + MAX_LEN)).trim());
  });

  orphanCloses.forEach((pos) => {
    const start = Math.max(0, pos - MAX_LEN);
    malformed.push(plain.substring(start, pos + 2).trim());
  });

  return malformed;
}

export function decimalValidator(maxDigits: number, decimalPlaces: number) {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === "") {
      return null; // let required validator handle empty
    }

    const value = control.value.toString().trim();

    const integerDigits = maxDigits - decimalPlaces;

    // Build regex dynamically
    const regex = new RegExp(`^-?\\d{1,${integerDigits}}(\\.\\d{0,${decimalPlaces}})?$`);

    if (!regex.test(value)) {
      return {
        invalidDecimal: {
          maxDigits,
          decimalPlaces,
        },
      };
    }

    return null;
  };
}

export function hexColourValidator(control: AbstractControl): ValidationErrors | null {
  if (isEmptyInputValue(control.value)) {
    return null;
  }
  const isValid = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(control.value.trim());
  return isValid ? null : { invalidHexColour: true };
}

export function lessThanSiblingControlValidator(
  siblingControlName: string,
  errorKey: string = "lessThanSibling"
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isEmptyInputValue(control.value)) {
      return null;
    }

    const siblingControl = control.parent?.get(siblingControlName);
    if (!siblingControl || isEmptyInputValue(siblingControl.value)) {
      return null;
    }

    const value = Number(control.value);
    const siblingValue = Number(siblingControl.value);
    if (!Number.isFinite(value) || !Number.isFinite(siblingValue)) {
      return null;
    }

    return value < siblingValue
      ? null
      : {
          [errorKey]: {
            value,
            siblingControlName,
            siblingValue,
          },
        };
  };
}

/**
 * Validator for a FormArray of FormGroups: flags when the named control repeats across rows.
 *
 * Used for response option group internal values, which identify responses and so must be unique
 * within a group. Blank values are ignored — `Validators.required` on the row reports those.
 */
export function uniqueFieldInArrayValidator(
  fieldName: string,
  errorKey = "duplicateValues"
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const rows = (control.value as Record<string, unknown>[] | null) ?? [];
    const seen = new Set<string>();
    const duplicates = new Set<string>();

    for (let i = 0; i < rows.length; i++) {
      const raw = rows[i]?.[fieldName];
      if (raw === null || raw === undefined || `${raw}`.trim() === "") {
        continue;
      }
      const key = `${raw}`.trim();
      if (seen.has(key)) {
        duplicates.add(key);
      }
      seen.add(key);
    }

    return duplicates.size ? { [errorKey]: { fieldName, duplicates: [...duplicates] } } : null;
  };
}
